"use client";

import { useCallback, useTransition } from "react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { Check, Globe, GlobeLock } from "lucide-react";
import { Button } from "./button";
import { CustomTooltip } from "./tooltip";
import { locales, type Locale } from "@/i18n/settings";
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuTrigger
} from "./dropdown-menu";

export function LanguageSwitcher() {
   const locale = useLocale();
   const router = useRouter();
   const pathname = usePathname();
   const translation = useTranslations("LanguageSwitcher");
   const [isPending, startTransition] = useTransition();

   const toggleLocale = useCallback(
      (value: Locale) => {
         startTransition(() => {
            router.push(pathname || "/", { locale: value });
         });
      },
      [pathname, router]
   );

   return (
      <DropdownMenu>
         <CustomTooltip
            text={translation(locale === "vi" ? "vietnamese" : "english")}
            delayDuration={1000}
         >
            <DropdownMenuTrigger asChild>
               <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 relative rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                  disabled={isPending}
               >
                  {locale === "vi" ? <GlobeLock /> : <Globe className="h-4 w-4" />}
               </Button>
            </DropdownMenuTrigger>
         </CustomTooltip>
         <DropdownMenuContent align="end">
            {locales.map((lang) => (
               <DropdownMenuItem
                  key={lang}
                  onClick={() => toggleLocale(lang)}
                  className={`flex items-center gap-2 justify-between ${
                     locale === lang ? "bg-accent font-medium" : ""
                  }`}
                  aria-selected={locale === lang}
               >
                  {translation(lang)}
                  {locale === lang && <Check className="h-4 w-4" />}
               </DropdownMenuItem>
            ))}
         </DropdownMenuContent>
      </DropdownMenu>
   );
}
